import React, { useRef, useState } from "react";
import { CircularProgress, IconButton, TextField, Tooltip } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useDispatch, useSelector } from "react-redux";
import { sendChatMessage, uploadChatAttachment } from "../../store/actions/chatbot";

const ACCEPTED_TYPES = "image/*,.xlsx,.xls";
const MAX_IMAGE_SIZE  = 10 * 1024 * 1024;
const MAX_EXCEL_SIZE  = 5 * 1024 * 1024;

function MessageInput() {
  const dispatch  = useDispatch();
  const loading   = useSelector((s) => s.chatbot.chatLoading);
  const uploading = useSelector((s) => s.chatbot.chatAttachmentUploading);
  const [text, setText] = useState("");
  const [attachError, setAttachError] = useState("");
  const fileInputRef = useRef(null);

  const busy = loading || uploading;

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    dispatch(sendChatMessage(trimmed));
    setText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAttachClick = () => {
    if (busy) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file(s) later
    if (!files.length) return;

    setAttachError("");

    // Uploaded sequentially (not Promise.all) so attachment messages land in
    // the chat in selection order, and one bad file doesn't abort the rest.
    for (const file of files) {
      const isImage = file.type.startsWith("image/");
      const maxSize = isImage ? MAX_IMAGE_SIZE : MAX_EXCEL_SIZE;
      if (file.size > maxSize) {
        setAttachError(`"${file.name}" is too large (max ${maxSize / (1024 * 1024)} MB) — skipped.`);
        continue;
      }
      try {
        await dispatch(uploadChatAttachment(file));
      } catch (err) {
        setAttachError(err?.response?.data?.error || `Upload of "${file.name}" failed. Please try again.`);
      }
    }
  };

  return (
    <div className="chat-message-input" style={{ flexDirection: "column", alignItems: "stretch" }}>
      {attachError && (
        <div style={{ fontSize: "11px", color: "#c62828", padding: "0 4px 4px" }}>{attachError}</div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Tooltip title="Attach image(s) or an Excel file">
          <span>
            <IconButton onClick={handleAttachClick} disabled={busy} sx={{ flexShrink: 0 }}>
              {uploading ? <CircularProgress size={18} /> : <AttachFileIcon />}
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={4}
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 1000))}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          disabled={busy}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
        />
        <IconButton
          onClick={handleSend}
          disabled={busy || !text.trim()}
          color="primary"
          sx={{ flexShrink: 0 }}
        >
          {loading ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </div>
    </div>
  );
}

export default MessageInput;
